import "./mark-delivered-modal.css";

import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostMarkDelivered } from "../../hooks/use-post-mark-delivered.ts";

export const MarkDeliveredModal = ({
	onClose,
	isOpen,
}: {
	onClose: () => void;
	isOpen: boolean;
}) => {
	const deliveredDateId = useId();
	const deliveredTimeId = useId();
	const dateErrorId = `${deliveredDateId}_error`;
	const timeErrorId = `${deliveredTimeId}_error`;
	const futureErrorId = `${deliveredDateId}_future`;
	const params = useParams();
	const { markDelivered, isSavingMarkDelivered } = usePostMarkDelivered();

	const [deliveredDate, setDeliveredDate] = useState<Date | null>(null);
	const [deliveredTime, setDeliveredTime] = useState<Date | null>(null);
	const [submitted, setSubmitted] = useState(false);

	// Combine the selected date and time into a single instant so it can be
	// validated against "now" and sent as a UTC ISO string.
	const completedTime =
		deliveredDate && deliveredTime
			? new Date(
					deliveredDate.getFullYear(),
					deliveredDate.getMonth(),
					deliveredDate.getDate(),
					deliveredTime.getHours(),
					deliveredTime.getMinutes(),
					deliveredTime.getSeconds(),
				)
			: null;
	const isFuture = completedTime ? completedTime.getTime() > Date.now() : false;

	const showDateError = submitted && !deliveredDate;
	const showTimeError = submitted && !deliveredTime;
	const showFutureError = submitted && isFuture;

	const describedBy = (fieldErrorId: string, showFieldError: boolean) =>
		[
			showFieldError ? fieldErrorId : null,
			showFutureError ? futureErrorId : null,
		]
			.filter(Boolean)
			.join(" ") || undefined;

	const handleClose = () => {
		setDeliveredDate(null);
		setDeliveredTime(null);
		setSubmitted(false);
		onClose();
	};

	const handleConfirm = () => {
		setSubmitted(true);
		if (!completedTime || isFuture) {
			return;
		}
		markDelivered({
			poNumber: params["poNumber"] ?? "",
			completedTime: completedTime.toISOString(),
		});
		handleClose();
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			className={"mark-delivered-modal"}
			confirmText={"Confirm Delivery"}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={handleConfirm}
			showButtonsDisabled={isSavingMarkDelivered}
			title={"LTL Delivered Details"}
			type={"confirmation"}
			width={300}
		>
			<div className={"mark-delivered-form"}>
				<div className={"mark-delivered-row"}>
					<div className={"mark-delivered-field mark-delivered-date"}>
						<Label editorId={deliveredDateId}>
							Delivery date
							<span className={"required"}>&nbsp;*</span>
						</Label>
						<DatePicker
							ariaDescribedBy={describedBy(dateErrorId, showDateError)}
							id={deliveredDateId}
							max={new Date()}
							onChange={(event) => setDeliveredDate(event.target.value)}
							valid={!showDateError && !showFutureError}
							value={deliveredDate}
						/>
						{showDateError ? (
							<Error id={dateErrorId}>Delivery date is required.</Error>
						) : null}
					</div>
					<div className={"mark-delivered-field mark-delivered-time"}>
						{/* TimePicker puts `id` on both its wrapper and input, so a
						    `for` label would target the wrapper; label via aria instead. */}
						<Label id={`${deliveredTimeId}_label`}>
							Time<span className={"required"}>*</span>
						</Label>
						<TimePicker
							ariaDescribedBy={describedBy(timeErrorId, showTimeError)}
							ariaLabelledBy={`${deliveredTimeId}_label`}
							id={deliveredTimeId}
							onChange={(event) => setDeliveredTime(event.value)}
							valid={!showTimeError && !showFutureError}
							value={deliveredTime}
						/>
						{showTimeError ? (
							<Error id={timeErrorId}>Time is required.</Error>
						) : null}
					</div>
				</div>

				{showFutureError ? (
					<Error id={futureErrorId}>
						Delivery date and time cannot be in the future.
					</Error>
				) : null}
			</div>
		</TraxModal>
	);
};
